import React, { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  GridReadyEvent,
  CellEditingStartedEvent,
  CellValueChangedEvent,
  CellEditingStoppedEvent,
  SelectionChangedEvent,
  FilterChangedEvent,
  SortChangedEvent,
  PaginationChangedEvent,
  RowClickedEvent,
  CellMouseOverEvent,
  CellMouseOutEvent,
  GridPreDestroyedEvent,
  ColumnResizedEvent,
  ColumnMovedEvent,
  ColumnPinnedEvent,
  ColumnVisibleEvent,
  FirstDataRenderedEvent,
  IServerSideDatasource,
  ColDef,
  GetMainMenuItems
} from 'ag-grid-community';

import { useEditLocks } from '../hooks/useEditLocks';
import { useGridPopupCleanup } from '../hooks/useGridPopupCleanup';
import type { BaseRecord } from '../types/api';

// AG Grid Props Interface
interface InternalAGGridReactProps {
  columnsConfig: ColDef[];
  pageSize: number;
  onCellEditingStarted: (event: CellEditingStartedEvent) => void;
  onCellValueChanged: (event: CellValueChangedEvent) => void;
  datasource: IServerSideDatasource;
  tableName: string;
  schema: string;
  gridName: string;
  gridContext: {
    tableName: string;
    schema: string;
    isClientSideRow: (data: BaseRecord) => boolean;
    removeClientSideRow?: (id: number) => void;
    [key: string]: any;
  };
  onGridReady: (event: GridReadyEvent) => void;
  rowSelection?: 'single' | 'multiple';
  showPagination: boolean;
  heightLayout: 'normal' | 'autoHeight';
  /** Extra pixels taken by page chrome above the grid (e.g. a filter bar), subtracted from the viewport height calc. */
  heightOffset?: number;
  /** Per-table zoom factor (REG-653): scales row/header heights, fonts, and cell padding. */
  zoom?: number;
  onRowClicked?: (event: RowClickedEvent) => void;
  onCellMouseOver?: (event: CellMouseOverEvent) => void;
  onCellMouseOut?: (event: CellMouseOutEvent) => void;
  onSelectionChanged?: (selectedRows: BaseRecord[]) => void;
  onFilterChanged?: (filterModel: any) => void;
  onSortChanged?: (sortModel: any[]) => void;
  onPaginationChanged?: (paginationData: { currentPage: number; pageSize: number }) => void;
  onColumnResized?: (event: ColumnResizedEvent) => void;
  onColumnVisible?: (event: ColumnVisibleEvent) => void;
  /** Column reordered by drag (REG-828 saves the user's layout on `finished`). */
  onColumnMoved?: (event: ColumnMovedEvent) => void;
  onColumnPinned?: (event: ColumnPinnedEvent) => void;
  onFirstDataRendered?: (event: FirstDataRenderedEvent) => void;
  singleClickEdit?: boolean;
  /** Column three-dot menu items (REG-802 adds a stat line). */
  getMainMenuItems?: GetMainMenuItems;
}

// Sort model interface
interface SortModel {
  colId: string;
  sort: 'asc' | 'desc';
  sortIndex?: number;
}

/* eslint-disable react/prop-types */ 
const InternalAGGridReact = React.memo<InternalAGGridReactProps>(({
    columnsConfig,
    pageSize,
    onCellEditingStarted,
    onCellValueChanged,
    datasource,
    tableName,
    schema,
    gridContext,
    onGridReady,
    rowSelection,
    showPagination,
    heightLayout,
    heightOffset,
    zoom = 1,
    onRowClicked,
    onCellMouseOver,
    onCellMouseOut,
    onSelectionChanged,
    onFilterChanged,
    onSortChanged,
    onPaginationChanged,
    onColumnResized,
    onColumnVisible,
    onColumnMoved,
    onColumnPinned,
    onFirstDataRendered,
    singleClickEdit = true,
    getMainMenuItems,
  }) => {
    const getRowId = useCallback((params: { data: BaseRecord }) => String(params.data.id), []);
    const gridRef = useRef<AgGridReact>(null);
    const popupParent = useMemo<HTMLElement | undefined>(
      () => (typeof document === 'undefined' ? undefined : document.body),
      [],
    );

    // Live edit locks. Keyed on the table, not the grid, so the same record opened in a
    // second grid or in a side panel sees the same locks. Lock changes repaint cells
    // imperatively — turning them into state would re-render the grid and purge the
    // server-side row store.
    const handleLocksChanged = useCallback(() => {
      gridRef.current?.api?.refreshCells({ force: true, suppressFlash: true });
    }, []);

    const { getLockOwner, acquire, release } = useEditLocks({
      schema,
      tableName,
      onChange: handleLocksChanged,
    });

    const defaultColDef = useMemo<ColDef>(
      () => ({
        resizable: true,
        // Realtime transactions are frequent during imports/migrations. AG Grid's
        // change flash turns those background updates into a continuously blinking table.
        enableCellChangeFlash: false,
        // cellClassRules, not cellClass: generated columns define their own cellClass
        // (columnConfigGenerators), and a colDef property replaces the defaultColDef one
        // outright — the highlight class silently never made it onto the cell. Nothing sets
        // cellClassRules per column, and AG Grid re-evaluates the rules on refreshCells.
        cellClassRules: {
          'live-cell-editing': (params: any) =>
            Boolean(getLockOwner(params?.data?.id, params?.column?.getColId())),
        },
        tooltipValueGetter: (params: any) => {
          const owner = getLockOwner(params?.data?.id, params?.column?.getColId());
          return owner ? `Currently being edited by ${owner}` : null;
        },
        editable: (params: any) => {
          const isEditableField = params?.colDef?.context?.editable;
          if (!isEditableField) {
            return false;
          }
          return !getLockOwner(params?.data?.id, params?.column?.getColId());
        },
      }),
      [getLockOwner]
    );

    const [lastLoaded, setLastLoaded] = useState<number>(Date.now());

    useEffect(() => {
      // Reload data when the user revisits the tab if more than 5 minutes have elapsed
      const handleVisibilityChange = (): void => {
        if (
          document.visibilityState === 'visible' &&
          Date.now() - lastLoaded > 5 * 60 * 1000
        ) {
          // TODO show a manual refresh button instead of auto-reloading
          // gridRef.current?.api?.refreshServerSide();
          setLastLoaded(Date.now());
          // console.log('Data reloaded');
        }
      };
  
      document.addEventListener('visibilitychange', handleVisibilityChange);
  
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }, [lastLoaded]);
  
    // Row-level live updates (insert/update/delete) are handled by useLiveGridUpdates,
    // which owns the grid's filters, sort and paging. Cross-user edit locks are handled
    // by useEditLocks above.
  
    const onCellEditingStartedInternal = useCallback((event: CellEditingStartedEvent): void => {
      onCellEditingStarted(event);
      // Client-side rows are unsaved and local to this tab — nobody else can be in them.
      if (!event?.context?.isClientSideRow(event.data)) {
        acquire(event.data.id, event.column.getColId());
      }
    }, [acquire, onCellEditingStarted]);

    const onCellEditingStopped = useCallback((event: CellEditingStoppedEvent): void => {
      if (!event?.context?.isClientSideRow(event.data)) {
        release(event.data.id, event.column.getColId());
      }
    }, [release]);
  
    const onSelectionChangedInternal = useCallback((event: SelectionChangedEvent): void => {
      if (onSelectionChanged) {
        const selectedRows = event.api.getSelectedRows();
        onSelectionChanged(selectedRows);
      }
    }, [onSelectionChanged]);
  
    // Close the column chooser / menus with the grid (they live on document.body).
    const onGridPreDestroyed: (event: GridPreDestroyedEvent) => void = useGridPopupCleanup(() => gridRef.current?.api);

    // Handle filter changes
    const onFilterChangedInternal = useCallback((event: FilterChangedEvent): void => {
      if (onFilterChanged) {
        const filterModel = event.api.getFilterModel();
        onFilterChanged(filterModel);
      }
    }, [onFilterChanged]);

    // Handle sort changes
    const onSortChangedInternal = useCallback((event: SortChangedEvent): void => {
      if (onSortChanged) {
        const sortModel: SortModel[] = event.api.getColumnState()
          .filter(col => col.sort !== null)
          .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
          .map(col => ({
            colId: col.colId!,
            sort: col.sort as 'asc' | 'desc',
            sortIndex: col.sortIndex || undefined
          }));
        onSortChanged(sortModel);
      }
    }, [onSortChanged]);

    // Handle pagination changes
    const onPaginationChangedInternal = useCallback((event: PaginationChangedEvent): void => {
      if (onPaginationChanged) {
        const currentPage = event.api.paginationGetCurrentPage();
        const pageSize = event.api.paginationGetPageSize();
        onPaginationChanged({ currentPage, pageSize });
      }
    }, [onPaginationChanged]);

    const containerStyle: React.CSSProperties = {
      width: '100%',
      // The theme's sizing vars are calc()-scaled by this factor (REG-653).
      ['--sky-grid-zoom' as string]: zoom,
    };
    
    if (heightLayout !== 'autoHeight') {
      // calc accounts for header + padding in .sky-content wrapper
      containerStyle.height = `calc(100vh - var(--header-h) - ${88 + (heightOffset ?? 0)}px)`;
    }

    return (
      <div className="ag-theme-alpine" style={containerStyle}>
        <AgGridReact
          className='ag-theme-alpine'
          domLayout={heightLayout}
          rowHeight={Math.round(34 * zoom)}
          headerHeight={Math.round(32 * zoom)}
          // Anchor editor popups to the document so a dropdown opened on one of
          // the last rows is not clipped by the grid's own viewport.
          popupParent={popupParent}
          // AG Grid animates row opacity by default. Realtime server-side refreshes can
          // overlap that transition and leave the whole grid visibly blinking.
          animateRows={false}
          onGridReady={onGridReady}
          context={gridContext}
          ref={gridRef}
          onGridPreDestroyed={onGridPreDestroyed}
          defaultColDef={defaultColDef}
          columnDefs={columnsConfig}
          rowSelection={rowSelection}
          pagination={showPagination}
          paginationPageSize={pageSize}
          // Server-side cache blocks must match visible pages. If the default
          // 100-row block is used with a 50-row page, AG Grid treats page 2 as
          // already loaded even when the datasource only returned page 1.
          cacheBlockSize={pageSize}
          onCellValueChanged={onCellValueChanged}
          rowModelType="serverSide"
          getRowId={getRowId}
          serverSideDatasource={datasource}
          singleClickEdit={singleClickEdit}
          onCellEditingStarted={onCellEditingStartedInternal}
          onCellEditingStopped={onCellEditingStopped}
          undoRedoCellEditing={true}
          undoRedoCellEditingLimit={10}
          suppressCsvExport={true}
          suppressExcelExport={true}
          tooltipShowDelay={100}
          tooltipHideDelay={2000}
          tooltipInteraction={true}
          stopEditingWhenCellsLoseFocus={true}
          suppressColumnVirtualisation={true}
          rowBuffer={100}
          onSelectionChanged={onSelectionChangedInternal}
          onRowClicked={onRowClicked}
          onCellMouseOver={onCellMouseOver}
          onCellMouseOut={onCellMouseOut}
          onFilterChanged={onFilterChangedInternal}
          onSortChanged={onSortChangedInternal}
          onPaginationChanged={onPaginationChangedInternal}
          onColumnResized={onColumnResized}
          onColumnVisible={onColumnVisible}
          onColumnMoved={onColumnMoved}
          onColumnPinned={onColumnPinned}
          onFirstDataRendered={onFirstDataRendered}
          getMainMenuItems={getMainMenuItems}
        />
      </div>
    );
  });
  
InternalAGGridReact.displayName = 'InternalAGGridReact';
  
export default InternalAGGridReact;
